import { DataTableProvider } from 'components/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import { ContactResponse } from 'models/contacts'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { DECommonTable } from 'components/DataExpansionComponents/Common/CommonTable/CommonTable'
import apiService from 'services/api'
import { useUnmatchedDoorsTableColumns } from './UnmatchedDoorsTableColumns'
import { IContactCompany } from 'models/contact_companies'
import { StandardSummaryResponse } from 'models/paginated-response'
import { FilterIdentifier } from 'models/saved_view'
import confirm from '../../dialogConfirm'
import { toast } from 'react-toastify'
import { useMemo, useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { IDoorProxy } from 'models/contact_companies'
import { ActionButtons } from 'components/Buttons/ActionButtons'
import { EditDoorProxyModal } from 'components/Modals/CompanyModal/EditDoorProxyModal'
import { UnmatchedExportModal } from './UnmatchedExportModal'
import { SubCategory } from 'models/companies'
import { DoorsFilterset } from 'components/DataExpansionComponents/Common/CommonTable/DoorsFilterset'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'

type SortableFields = (keyof ContactResponse | string)[]

const sortableFields: SortableFields = [
  'name',
  'domain',
  'address',
  'city',
  'state',
  'zipcode',
  'country',
  'phone_number',
  'extension',
  'sales_stages',
  'note_count',
  'contact_count',
  'chain_size',
  // TODO: Uncomment this when we have a better way to handle deal counts
  // 'deal_count',
]

interface UnmatchedDoorsTableProps {
  setTotalRowsCount?: (count: StandardSummaryResponse) => void
  pageParamName?: string
  baseFilters?: Record<string, any>
  tableKey: string
  filterIdentifierModifier: FilterIdentifier
  hideCreateCompanyButton?: boolean
  chainProxy?: IContactCompany
  subCategory?: SubCategory
  useAllDoorProxies?: boolean
  AfterSearchComponent?: React.ReactNode
}

function UnmatchedDoorsTableComponent(props: UnmatchedDoorsTableProps) {
  const { tableKey, useAllDoorProxies = false, ...rest } = props
  const api = apiService()
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()
  const baseCols = useUnmatchedDoorsTableColumns({
    sorting,
    inCampaigns: !!props.baseFilters?.campaigns,
  })
  const [doorToEdit, setDoorToEdit] = useState<IDoorProxy | null>(null)

  async function handleDeleteDoor(doorProxyId: number) {
    const res = await confirm(
      'Are you sure you want to delete this door?',
      'Delete Door',
      undefined,
      {
        buttonVariant: 'danger',
        confirmLabel: 'Delete Door',
      }
    )

    if (res) {
      await api.deleteDoorProxy(doorProxyId)
      toast.success('Door deleted successfully.')
    }
  }

  const columns = useMemo<ColumnDef<IDoorProxy>[]>(
    () => [
      ...baseCols,
      {
        header: 'Actions',
        size: 100,
        meta: {
          isEditable: false,
          isLastColumn: true,
        },
        cell: (info) => {
          const door = info.row.original

          return (
            <ActionButtons
              className={'justify-start'}
              onDelete={() => handleDeleteDoor(door.id)}
              onEdit={() => setDoorToEdit(door)}
            />
          )
        },
      },
    ],
    [handleDeleteDoor, setDoorToEdit, baseCols]
  )

  // Get effective subCategory from props - use subCategory directly or from chainProxy
  const effectiveSubCategory = useMemo(() => {
    return props.subCategory || props.chainProxy?.sub_category
  }, [props.subCategory, props.chainProxy])

  if (!effectiveSubCategory) {
    return null
  }

  const apiGetListAction = useAllDoorProxies
    ? (params: any) => {
        return api.getAllDoorProxies({
          ...params,
          sub_category_id: effectiveSubCategory.id,
        })
      }
    : props.chainProxy
      ? api.getDoorProxiesOfChainProxy.bind(null, props.chainProxy?.id)
      : (params: any) => {
          return api.getAllDoorProxies({
            ...params,
            sub_category_id: effectiveSubCategory.id,
          })
        }

  const apiGetSummaryAction = useAllDoorProxies
    ? (params: any) => {
        return api.getAllDoorProxiesSummary({
          ...params,
          sub_category_id: effectiveSubCategory.id,
        })
      }
    : props.chainProxy
      ? api.getDoorProxiesOfChainProxySummary.bind(null, props.chainProxy?.id)
      : (params: any) => {
          return api.getAllDoorProxiesSummary({
            ...params,
            sub_category_id: effectiveSubCategory.id,
          })
        }

  return (
    <>
      <DECommonTable
        {...rest}
        hideCreateCompanyButton={true}
        tableKey={tableKey}
        columns={columns}
        sortableFields={sortableFields}
        filterIdentifier={'UNMATCHED_DOORS_TABLE'}
        apiGetListAction={apiGetListAction}
        // We already filter out on the apiActions but we also use the baseFilters
        // to filter other nested actions like createDeals...
        baseFilters={{
          sub_category_id: effectiveSubCategory.id,
          ...props.baseFilters,
        }}
        apiGetSummaryAction={apiGetSummaryAction}
        companyTypeSlug={effectiveSubCategory.slug}
        tableTypeSlug={'unmatched-doors-table'}
        searchPlaceholder="Search unmatched doors by name"
        isDoorsTable={true}
        setTotalRowsCount={props.setTotalRowsCount}
        FilterSet={(filterProps) => (
          <DoorsFilterset
            {...filterProps}
            subCategoryId={effectiveSubCategory.id}
          />
        )}
        ExportModal={(exportModalProps) => (
          <UnmatchedExportModal
            {...exportModalProps}
            subCategoryId={effectiveSubCategory.id}
          />
        )}
        AfterSearchComponent={props.AfterSearchComponent}
        chainProxyIdAccessorKey="chain_proxy.id"
        doorProxyIdAccessorKey="id"
      />
      <EditDoorProxyModal
        doorProxy={doorToEdit}
        show={!!doorToEdit}
        setShow={(show) => {
          if (!show) {
            setDoorToEdit(null)
          }
        }}
      />
    </>
  )
}

export function UnmatchedDoorsTable(
  props: UnmatchedDoorsTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  return (
    <ColumnsStoreProvider
      savedPresetsIdentifier={'UNMATCHED_DOORS_TABLE'}
      tableKey={tableKey}
      defaultColumnVisibility={{
        name: true,
        domain: false,
        address: true,
        city: true,
        state: true,
        zipcode: true,
        country: false,
        phone_number: true,
        chain_size: false,
        campaigns: false,
        sales_stages: false,
        account_owners: false,
        door_taglist: true,
        chain_proxy_taglist: false,
        note_count: false,
        door_distributors: true,
        chain_proxy_distributors: false,
        ...defaultColumnVisibility,
      }}
      initialColumnOrder={[
        'name',
        'address',
        'city',
        'state',
        'zipcode',
        'phone_number',
        'door_taglist',
        'door_distributors',
      ]}
      preferredGroupOrder={[
        'Target Attributes',
        'School Insights',
        'Campaigns and Deals',
        'Dining Services',
      ]}
    >
      <DataTableProvider tableKey={tableKey}>
        <UnmatchedDoorsTableComponent {...rest} tableKey={tableKey} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
